/**
 * AI3D Fab-in-Place - Main Application
 * "Lovable for AI-generated 3D assets"
 *
 * Converts messy AI-generated 3D objects (PLY/GLB with vertex colors, huge polycount)
 * into game-ready GLB assets (UVs, baked textures, optimized polycount).
 */

import { useState } from 'react';
import { FabricationPanel } from './components/FabricationPanel';
import { ABViewer } from './components/ABViewer';
import { MetricsPanel } from './components/MetricsPanel';
import { Header } from './components/Header';
import { JobHistory } from './components/JobHistory';
import { ArchitectureDiagram } from './components/ArchitectureDiagram';
import { useFabStore } from './store';

type ViewTab = 'fabricate' | 'architecture';

function App() {
  const { isPanelOpen, setIsPanelOpen, rawModelUrl } = useFabStore();
  const [activeTab, setActiveTab] = useState<ViewTab>('architecture');

  // Show architecture by default, switch to fabricate when model loaded
  const showArchitecture = activeTab === 'architecture' && !rawModelUrl;

  return (
    <div className="min-h-screen bg-fab-darker flex flex-col">
      <Header />

      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Fabrication Controls */}
        <aside
          className={`
            ${isPanelOpen ? 'w-80' : 'w-0'}
            transition-all duration-300 ease-in-out
            bg-fab-dark border-r border-slate-700
            flex flex-col overflow-hidden
          `}
        >
          {isPanelOpen && (
            <>
              <FabricationPanel />
              <JobHistory />
            </>
          )}
        </aside>

        {/* Toggle Panel Button */}
        <button
          onClick={() => setIsPanelOpen(!isPanelOpen)}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-20
                     bg-fab-dark border border-slate-700 border-l-0
                     rounded-r-lg px-1 py-4 text-slate-400 hover:text-white
                     hover:bg-slate-800 transition-colors"
          style={{ left: isPanelOpen ? '320px' : '0' }}
        >
          {isPanelOpen ? '◀' : '▶'}
        </button>

        {/* Main Content */}
        <main className="flex-1 flex flex-col overflow-hidden">
          {/* Tab Navigation */}
          <div className="flex items-center justify-center gap-2 p-2 bg-fab-dark border-b border-slate-700">
            <button
              onClick={() => setActiveTab('architecture')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === 'architecture'
                  ? 'bg-fab-primary text-white'
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white'
              }`}
            >
              🏗️ Architecture
            </button>
            <button
              onClick={() => setActiveTab('fabricate')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === 'fabricate'
                  ? 'bg-fab-primary text-white'
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white'
              }`}
            >
              ⚙️ Fabricate
            </button>
          </div>

          {/* Content Area */}
          {showArchitecture || activeTab === 'architecture' ? (
            <div className="flex-1 overflow-auto p-6">
              {/* Hero Section */}
              <div className="text-center mb-8">
                <h1 className="text-4xl font-bold text-white mb-3">
                  AI3D Fab-in-Place
                </h1>
                <p className="text-xl text-fab-accent mb-2">
                  Lovable for AI-generated 3D assets
                </p>
                <p className="text-slate-400 max-w-2xl mx-auto">
                  Convert messy AI-generated 3D objects (PLY/GLB with vertex colors, huge polycount)
                  into game-ready GLB assets with UVs, baked textures, and optimized polycount.
                </p>
              </div>

              {/* Architecture Diagram */}
              <ArchitectureDiagram />

              {/* CTA */}
              <div className="mt-8 text-center">
                <button
                  onClick={() => setActiveTab('fabricate')}
                  className="px-8 py-3 bg-gradient-to-r from-fab-primary to-fab-secondary
                             text-white font-semibold rounded-lg hover:opacity-90 transition-opacity"
                >
                  🚀 Start Fabricating
                </button>
              </div>

              {/* Tech Stack Footer */}
              <div className="mt-12 pt-6 border-t border-slate-700">
                <p className="text-center text-slate-500 text-sm mb-4">
                  Built with enterprise-grade infrastructure
                </p>
                <div className="flex justify-center gap-8 text-slate-400">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">☔</span>
                    <span className="text-sm">Raindrop</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xl">⚡</span>
                    <span className="text-sm">Vultr GPU</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xl">🔺</span>
                    <span className="text-sm">Three.js</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xl">⚛️</span>
                    <span className="text-sm">React</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              <ABViewer />
              <MetricsPanel />
            </>
          )}
        </main>
      </div>
    </div>
  );
}

export default App;
